/**
 * Funciones auxiliares
 * Utilidades generales para el sistema
 */
import {
  CHECK_STATE_COLORS,
  CHECK_STATE_LABELS,
  DEPOSIT_STATE_COLORS,
  DEPOSIT_STATE_LABELS,
  NOVEDAD_STATE_LABELS,
  ALERT_DAYS,
} from './constants';
import dayjs from '../config/dayjs';

/**
 * Obtener color según estado de cheque
 * @param {string} status - Estado del cheque
 * @returns {string} Color MUI
 */
export const getStatusColor = (status) => {
  return CHECK_STATE_COLORS[status] || 'default';
};

/**
 * Obtener label según estado de cheque
 * @param {string} status - Estado del cheque
 * @returns {string} Label en español
 */
export const getStatusLabel = (status) => {
  return CHECK_STATE_LABELS[status] || status;
};

/**
 * Obtener color según estado de depósito
 * @param {string} status - Estado del depósito
 * @returns {string} Color MUI
 */
export const getDepositStatusColor = (status) => {
  return DEPOSIT_STATE_COLORS[status] || 'default';
};

/**
 * Obtener label según estado de depósito
 * @param {string} status - Estado del depósito
 * @returns {string} Label en español
 */
export const getDepositStatusLabel = (status) => {
  return DEPOSIT_STATE_LABELS[status] || status;
};

/**
 * Obtener label según estado de novedad
 * @param {string} status - Estado de la novedad
 * @returns {string} Label en español
 */
export const getNovedadStatusLabel = (status) => {
  return NOVEDAD_STATE_LABELS[status] || status;
};

/**
 * Calcular diferencia en días entre dos fechas
 * @param {Date|string} date1 - Primera fecha
 * @param {Date|string} date2 - Segunda fecha
 * @returns {number} Diferencia en días
 */
export const calculateDaysDifference = (date1, date2) => {
  if (!date1 || !date2) return 0;

  try {
    return dayjs(date1).diff(dayjs(date2), 'day');
  } catch (error) {
    console.error('Error al calcular diferencia de días:', error);
    return 0;
  }
};

/**
 * Verificar si un cheque es post-fechado
 * @param {Date|string} fechaPago - Fecha de pago del cheque
 * @returns {boolean} true si es post-fechado
 */
export const isPostDated = (fechaPago) => {
  if (!fechaPago) return false;

  try {
    const payDate = dayjs(fechaPago);
    const today = dayjs().startOf('day');

    return payDate.isAfter(today);
  } catch (error) {
    console.error('Error al verificar post-fechado:', error);
    return false;
  }
};

/**
 * Verificar si una fecha ha vencido
 * @param {Date|string} date - Fecha a verificar
 * @returns {boolean} true si ha vencido
 */
export const isExpired = (date) => {
  if (!date) return false;

  try {
    const checkDate = dayjs(date);
    const today = dayjs().startOf('day');

    return checkDate.isBefore(today);
  } catch (error) {
    console.error('Error al verificar vencimiento:', error);
    return false;
  }
};

/**
 * Calcular días hasta vencimiento
 * @param {Date|string} date - Fecha de vencimiento
 * @returns {number} Días hasta vencimiento (negativo si ya venció)
 */
export const daysUntilExpiration = (date) => {
  if (!date) return 0;

  try {
    const expirationDate = dayjs(date);
    const today = dayjs().startOf('day');

    return expirationDate.diff(today, 'day');
  } catch (error) {
    console.error('Error al calcular días hasta vencimiento:', error);
    return 0;
  }
};

/**
 * Obtener nivel de alerta según días hasta vencimiento
 * @param {Date|string} date - Fecha de vencimiento
 * @returns {string} 'urgent' | 'warning' | 'info' | 'normal'
 */
export const getExpirationAlertLevel = (date) => {
  const days = daysUntilExpiration(date);

  if (days < 0) return 'expired';
  if (days <= ALERT_DAYS.URGENT) return 'urgent';
  if (days <= ALERT_DAYS.WARNING) return 'warning';
  if (days <= ALERT_DAYS.INFO) return 'info';

  return 'normal';
};

/**
 * Obtener color según nivel de alerta
 * @param {string} level - Nivel de alerta
 * @returns {string} Color MUI
 */
export const getAlertColor = (level) => {
  const colors = {
    expired: 'error',
    urgent: 'error',
    warning: 'warning',
    info: 'info',
    normal: 'success',
  };

  return colors[level] || 'default';
};

/**
 * Agrupar items por campo
 * @param {Array} items - Items a agrupar
 * @param {string} key - Campo por el cual agrupar
 * @returns {Object} Items agrupados
 */
export const groupBy = (items, key) => {
  if (!items || !Array.isArray(items)) return {};

  return items.reduce((result, item) => {
    const groupKey = item[key];
    if (!result[groupKey]) {
      result[groupKey] = [];
    }
    result[groupKey].push(item);
    return result;
  }, {});
};

/**
 * Ordenar items por campo
 * @param {Array} items - Items a ordenar
 * @param {string} key - Campo por el cual ordenar
 * @param {string} order - 'asc' o 'desc'
 * @returns {Array} Items ordenados
 */
export const sortBy = (items, key, order = 'asc') => {
  if (!items || !Array.isArray(items)) return [];

  const sorted = [...items].sort((a, b) => {
    const aValue = a[key];
    const bValue = b[key];

    if (aValue < bValue) return order === 'asc' ? -1 : 1;
    if (aValue > bValue) return order === 'asc' ? 1 : -1;
    return 0;
  });

  return sorted;
};

/**
 * Filtrar items por búsqueda de texto
 * @param {Array} items - Items a filtrar
 * @param {string} searchText - Texto de búsqueda
 * @param {Array} fields - Campos en los que buscar
 * @returns {Array} Items filtrados
 */
export const filterByText = (items, searchText, fields = []) => {
  if (!items || !Array.isArray(items)) return [];
  if (!searchText) return items;

  const lowerSearch = searchText.toLowerCase();

  return items.filter((item) => {
    return fields.some((field) => {
      const value = item[field];
      if (!value) return false;

      return String(value).toLowerCase().includes(lowerSearch);
    });
  });
};

/**
 * Sumar totales de un array de items
 * @param {Array} items - Items a sumar
 * @param {string} field - Campo a sumar
 * @returns {number} Total
 */
export const sumBy = (items, field) => {
  if (!items || !Array.isArray(items)) return 0;

  return items.reduce((total, item) => {
    const value = parseFloat(item[field]) || 0;
    return total + value;
  }, 0);
};

/**
 * Obtener valores únicos de un campo
 * @param {Array} items - Items
 * @param {string} field - Campo
 * @returns {Array} Valores únicos
 */
export const uniqueBy = (items, field) => {
  if (!items || !Array.isArray(items)) return [];

  const unique = [...new Set(items.map((item) => item[field]))];
  return unique.filter((value) => value !== null && value !== undefined);
};

/**
 * Convertir objeto a query string
 * @param {Object} obj - Objeto a convertir
 * @returns {string} Query string
 */
export const objectToQueryString = (obj) => {
  if (!obj || typeof obj !== 'object') return '';

  const params = new URLSearchParams();

  Object.entries(obj).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== '') {
      params.append(key, value);
    }
  });

  return params.toString();
};

/**
 * Parsear query string a objeto
 * @param {string} queryString - Query string
 * @returns {Object} Objeto
 */
export const queryStringToObject = (queryString) => {
  if (!queryString) return {};

  const params = new URLSearchParams(queryString);
  const obj = {};

  params.forEach((value, key) => {
    obj[key] = value;
  });

  return obj;
};

/**
 * Debounce function
 * @param {Function} func - Función a ejecutar
 * @param {number} wait - Tiempo de espera en ms
 * @returns {Function} Función debounced
 */
export const debounce = (func, wait = 300) => {
  let timeout;

  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };

    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

/**
 * Generar ID único
 * @returns {string} ID único
 */
export const generateId = () => {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Clonar objeto profundo
 * @param {Object} obj - Objeto a clonar
 * @returns {Object} Objeto clonado
 */
export const deepClone = (obj) => {
  if (obj === null || typeof obj !== 'object') return obj;

  try {
    return JSON.parse(JSON.stringify(obj));
  } catch (error) {
    console.error('Error al clonar objeto:', error);
    return obj;
  }
};

/**
 * Verificar si dos objetos son iguales
 * @param {Object} obj1 - Primer objeto
 * @param {Object} obj2 - Segundo objeto
 * @returns {boolean} true si son iguales
 */
export const isEqual = (obj1, obj2) => {
  return JSON.stringify(obj1) === JSON.stringify(obj2);
};

/**
 * Descargar archivo blob
 * @param {Blob} blob - Blob del archivo
 * @param {string} filename - Nombre del archivo
 */
export const downloadBlob = (blob, filename) => {
  try {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error al descargar archivo:', error);
    throw error;
  }
};

export default {
  getStatusColor,
  getStatusLabel,
  getDepositStatusColor,
  getDepositStatusLabel,
  getNovedadStatusLabel,
  calculateDaysDifference,
  isPostDated,
  isExpired,
  daysUntilExpiration,
  getExpirationAlertLevel,
  getAlertColor,
  groupBy,
  sortBy,
  filterByText,
  sumBy,
  uniqueBy,
  objectToQueryString,
  queryStringToObject,
  debounce,
  generateId,
  deepClone,
  isEqual,
  downloadBlob,
};
